/**
 * video-popup.js
 * Lightweight, dependency-free pop-up player for "Style Class Shorts" / reels.
 * - Click a thumbnail -> opens a native <dialog> at that slide.
 * - Current slide autoplays MUTED (no need to start it).
 * - Sound button unmutes; one tap.
 * - Prev/Next + native swipe (CSS scroll-snap) move between reels (carousel).
 * - Close via X, Esc, or backdrop click. Videos pause/reset on close.
 * Progressive enhancement: with JS off, thumbnails are plain links to the media.
 */
(function () {
  'use strict';

  // iOS/Safari play HLS (.m3u8) natively and start almost instantly; a plain mp4
  // (non-faststart) makes iOS download the whole file before the first frame, so
  // the pop-up looked frozen on open. Shopify hosts an HLS playlist for every
  // uploaded video at a predictable URL, so on native-HLS browsers we add it as
  // the FIRST source. Desktop (Chrome/Firefox) can't play m3u8, so it just falls
  // through to the mp4 — same as before, and no hls.js (which stalled with many
  // concurrent reels).
  var NATIVE_HLS = (function () {
    try {
      // canPlayType('…mpegurl') lies on desktop Chrome (returns "maybe" but can't
      // actually play HLS), so gate on the browser: every iOS browser is WebKit
      // and plays HLS natively; on desktop only Safari does. Chrome/Firefox fall
      // through to the mp4.
      var ua = navigator.userAgent || '';
      var isIOS = /iP(hone|ad|od)/.test(ua) ||
        (/Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);
      var isSafari = /Safari/.test(ua) && !/(Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android)/.test(ua);
      if (!(isIOS || isSafari)) return false;
      return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
    } catch (e) { return false; }
  })();
  function shopifyHls(url) {
    if (!url) return null;
    var m = url.match(/(.*\/cdn\/shop\/videos\/c\/vp\/([a-f0-9]+)\/)/i);
    return m ? (m[1] + m[2] + '.m3u8') : null;
  }
  function upgradeToHls(v) {
    if (!NATIVE_HLS || !v || v._hlsUpgraded) return;
    var srcEl = v.querySelector('source');
    var mp4 = (srcEl && srcEl.getAttribute('src')) || v.currentSrc || v.getAttribute('src');
    var hls = shopifyHls(mp4);
    if (!hls) return;
    v._hlsUpgraded = true;
    var s = document.createElement('source');
    s.setAttribute('type', 'application/vnd.apple.mpegurl');
    s.setAttribute('src', hls);
    v.insertBefore(s, v.firstChild);   // first -> iOS picks HLS; desktop skips it
    try { v.load(); } catch (e) {}
  }

  function initRoot(root) {
    if (root.dataset.reelsInit === 'true') return;
    root.dataset.reelsInit = 'true';

    var dialog = root.querySelector('[data-reels-dialog]');
    var track = root.querySelector('[data-reels-track]');
    if (!dialog || !track) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-reel-slide]'));
    var soundBtn = dialog.querySelector('[data-reels-sound]');
    var playbtn = dialog.querySelector('[data-reels-play]');
    var swipeHint = dialog.querySelector('[data-reels-swipe-hint]');
    var swipeHintShowTimer = null, swipeHintHideTimer = null;
    var logoHost = dialog.querySelector('[data-reels-logo]');

    // Mobile: drop the store logo into the top-centre of the pop-up, matching
    // the nav logo's rendered size (the header's `.logo img { width }` rule only
    // applies inside .logo, so we measure and copy it onto the clone).
    function syncLogo() {
      if (!logoHost || !vmode()) return;
      var hdr = document.querySelector('.logo__image') || document.querySelector('.logotext');
      if (!hdr) return;
      var clone = hdr.cloneNode(true);
      clone.removeAttribute('class');
      clone.removeAttribute('id');
      if (hdr.tagName === 'IMG') {
        var w = hdr.getBoundingClientRect().width;
        clone.removeAttribute('width');
        clone.removeAttribute('height');
        if (w) clone.style.width = Math.round(w) + 'px';
        clone.style.height = 'auto';
      } else {
        var cs = window.getComputedStyle(hdr);
        clone.style.fontSize = cs.fontSize;
        clone.style.letterSpacing = cs.letterSpacing;
        clone.style.fontFamily = cs.fontFamily;
        clone.style.fontWeight = cs.fontWeight;
        clone.style.textTransform = cs.textTransform;
      }
      logoHost.innerHTML = '';
      logoHost.appendChild(clone);
    }
    var muted = true;
    var activeIndex = -1;   // which reel is currently showing (restart on change)

    function hideSwipeHint() {
      if (swipeHintShowTimer) { window.clearTimeout(swipeHintShowTimer); swipeHintShowTimer = null; }
      if (swipeHintHideTimer) { window.clearTimeout(swipeHintHideTimer); swipeHintHideTimer = null; }
      if (swipeHint) swipeHint.classList.remove('is-visible');
    }

    function showPlay(show) {
      if (playbtn) playbtn.classList.toggle('is-visible', !!show);
    }

    // Carousel axis: TikTok-style vertical swipe on mobile (full-screen slides),
    // horizontal + arrows on desktop. All position maths go through these so one
    // code path drives both axes.
    function vmode() { return window.matchMedia('(max-width: 767px)').matches; }
    function sStart(el) { return vmode() ? el.scrollTop : el.scrollLeft; }
    function vSize() { return vmode() ? track.clientHeight : track.clientWidth; }
    function sOff(s) { return vmode() ? s.offsetTop : s.offsetLeft; }
    function sSize(s) { return vmode() ? s.clientHeight : s.clientWidth; }
    function scrollToPos(pos, smooth) {
      var o = { behavior: smooth ? 'smooth' : 'auto' };
      o[vmode() ? 'top' : 'left'] = pos;
      track.scrollTo(o);
    }

    // Paint the poster as the slide background and only reveal the <video> once
    // it has a frame — kills Safari's black flash on open.
    slides.forEach(function (slide) {
      var v = slide.querySelector('video');
      if (!v) return;
      upgradeToHls(v);   // iOS: stream via HLS so it doesn't freeze buffering the mp4
      var poster = v.getAttribute('poster');
      if (poster) slide.style.backgroundImage = 'url("' + poster + '")';
      function reveal() { v.classList.add('is-ready'); }
      if (v.readyState >= 2) reveal();
      else {
        v.addEventListener('loadeddata', reveal, { once: true });
        v.addEventListener('playing', reveal, { once: true });
      }
      // Popup videos do NOT loop (only the thumbnails do). When the active clip
      // ends it goes to a clean black screen with the play button in the middle
      // (Tibi-style) — obvious it finished, and the play button restarts it.
      v.addEventListener('ended', function () {
        slide.classList.add('is-ended');            // black screen (CSS)
        if (v === videoAt(currentIndex())) showPlay(true);
      });
    });

    // Best-effort: does this video carry an audio track? (No reliable single API.)
    function hasAudio(v) {
      if (typeof v.mozHasAudio === 'boolean') return v.mozHasAudio;
      if (typeof v.webkitAudioDecodedByteCount === 'number') return v.webkitAudioDecodedByteCount > 0;
      if (v.audioTracks && typeof v.audioTracks.length === 'number') return v.audioTracks.length > 0;
      return true; // unknown -> leave the control enabled
    }
    // Keep the active slide filling the viewport while the card animates its
    // size (every slide reflows throughout the morph). Snap is paused meanwhile
    // so the manual scroll corrections don't fight it.
    function pinDuringMorph(idx) {
      var start = (window.performance && performance.now) ? performance.now() : Date.now();
      var DURATION = 420;                 // slightly longer than the CSS 0.34s morph
      var prevSnap = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      function step() {
        var now = (window.performance && performance.now) ? performance.now() : Date.now();
        track.scrollLeft = slides[idx].offsetLeft;
        if (now - start < DURATION) {
          requestAnimationFrame(step);
        } else {
          track.scrollLeft = slides[idx].offsetLeft;
          track.style.scrollSnapType = prevSnap || '';
        }
      }
      requestAnimationFrame(step);
    }

    // Make the card match the current media's aspect (portrait OR landscape).
    function fitCard(idx) {
      if (vmode()) return;   // mobile = full-screen TikTok slides, no card morph
      var slide = slides[idx];
      if (!slide) return;
      var media = slide.querySelector('video, img');
      if (!media) return;
      function apply() {
        var w = media.videoWidth || media.naturalWidth;
        var h = media.videoHeight || media.naturalHeight;
        if (!w || !h) return;
        var landscape = w > h;
        // Force one of two fixed forms. If the form changes, the card width
        // changes and every slide shifts — re-snap the current slide so the
        // next one doesn't bleed the previous frame.
        if (dialog.classList.contains('is-landscape') !== landscape) {
          dialog.classList.toggle('is-landscape', landscape);
          // Card width/height now animate (.morph). Hold the active slide
          // centred for the whole morph, not just one frame.
          pinDuringMorph(idx);
        }
      }
      if (media.videoWidth || media.naturalWidth) apply();
      else media.addEventListener(media.tagName === 'VIDEO' ? 'loadedmetadata' : 'load', apply, { once: true });
    }

    function videoAt(i) {
      return slides[i] ? slides[i].querySelector('video') : null;
    }

    function currentIndex() {
      if (vmode()) return activeIndex < 0 ? 0 : activeIndex;   // mobile has no scroll position
      // Closest slide to the viewport's centre (along the active axis).
      var mid = sStart(track) + vSize() / 2;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = sOff(slides[i]) + sSize(slides[i]) / 2;
        var d = Math.abs(c - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    function playOnly(idx) {
      // Landing on a DIFFERENT reel restarts it from the beginning (so swiping
      // away and back replays it). A re-settle on the same reel does not.
      var changed = idx !== activeIndex;
      activeIndex = idx;
      slides.forEach(function (slide, i) {
        var v = slide.querySelector('video');
        if (!v) return;
        if (i === idx) {
          if (changed) { try { v.currentTime = 0; } catch (e) {} }
          slide.classList.remove('is-ended');   // drop any leftover black screen
          v.muted = muted;
          var p = v.play();
          if (p && p.catch) p.catch(function () {
            // Audible autoplay blocked (no user activation) -> mute and retry so
            // the reel still plays; the speaker / tap can then unmute it.
            if (!muted) {
              muted = true;
              v.muted = true;
              if (soundBtn) {
                soundBtn.setAttribute('data-muted', 'true');
                soundBtn.setAttribute('aria-pressed', 'false');
              }
            }
            var p2 = v.play();
            if (p2 && p2.catch) p2.catch(function () {});
          });
        } else {
          v.pause();
        }
      });
      showPlay(false);
      fitCard(idx);
      // Sound button: enabled on any video slide, disabled only on image slides.
      // (We used to grey it until audio was "confirmed" via decoded-byte counters,
      // but those only tick while UNMUTED — so muting then swiping left the button
      // stuck greyed forever. Not worth it: just reflect the muted state.)
      var cur = videoAt(idx);
      if (soundBtn) {
        soundBtn.style.display = '';
        soundBtn.disabled = !cur;
        soundBtn.setAttribute('data-muted', String(muted));
        soundBtn.setAttribute('aria-pressed', String(!muted));
      }
    }

    // Tap the video to pause/resume; show a play button while paused.
    function togglePlay() {
      var v = videoAt(currentIndex());
      if (!v) return;
      if (v.paused) {
        var slide = v.closest ? v.closest('[data-reel-slide]') : null;
        // Finished -> play restarts from the beginning (clears the black screen).
        if (v.ended) { try { v.currentTime = 0; } catch (e) {} }
        if (slide) slide.classList.remove('is-ended');
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        showPlay(false);
      } else {
        v.pause();
        showPlay(true);
      }
    }

    // Tap the video: first tap on a clip with audio turns the sound ON (and
    // keeps playing); after that, tapping toggles play/pause. Silent clips just
    // toggle play/pause. Sound stays on across swipes via the shared `muted`.
    function onVideoClick() {
      var v = videoAt(currentIndex());
      if (!v) return;
      if (v.ended) { togglePlay(); return; }   // finished -> tap restarts from the black screen
      var audio = v.dataset.hasAudio === '1' || hasAudio(v);
      if (audio && muted) {
        muted = false;
        v.muted = false;
        if (v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        showPlay(false);
        if (soundBtn) {
          soundBtn.disabled = false;
          soundBtn.setAttribute('data-muted', 'false');
          soundBtn.setAttribute('aria-pressed', 'true');
        }
      } else {
        togglePlay();
      }
    }

    function updateNav(idx) {
      if (prev) prev.disabled = idx <= 0;
      if (next) next.disabled = idx >= slides.length - 1;
    }

    // Mobile only: position each slide by transform relative to the active one
    // (active = 0, below = +100%, above = -100%). `instant` skips the animation
    // (used on open). The transition lives in CSS.
    function layoutMobile(instant) {
      if (instant) slides.forEach(function (s) { s.style.transition = 'none'; });
      slides.forEach(function (s, i) {
        s.style.transform = 'translateY(' + ((i - activeIndex) * 100) + '%)';
      });
      if (instant) {
        void track.offsetHeight;   // force reflow, then restore so the next move animates
        slides.forEach(function (s) { s.style.transition = ''; });
      }
    }

    function goTo(idx, smooth) {
      if (idx < 0) idx = 0;
      if (idx > slides.length - 1) idx = slides.length - 1;
      if (!slides[idx]) return;
      playOnly(idx);        // sets activeIndex (its width also drives desktop slide positions)
      updateNav(idx);
      if (vmode()) layoutMobile(!smooth);
      else scrollToPos(sOff(slides[idx]), smooth);
    }

    // Pause AND mute (iOS can keep an audio session alive after a bare pause,
    // so muting guarantees silence). currentTime is left alone to avoid a
    // frame-jump during the close fade.
    function pauseAll() {
      slides.forEach(function (slide) {
        var v = slide.querySelector('video');
        if (v) { v.pause(); v.muted = true; }
      });
    }
    // Reset to first frame on open, so a reopened reel starts clean.
    function resetAll() {
      slides.forEach(function (slide) {
        var v = slide.querySelector('video');
        if (v) { try { v.currentTime = 0; } catch (e) {} }
      });
    }

    function open(idx) {
      resetAll();
      // Open with sound ON. The opening tap is a user gesture, so Chrome allows
      // audible autoplay; playOnly falls back to muted if it's ever blocked.
      muted = false;
      // Set the correct portrait/landscape form up-front (orientation is known
      // from the slide's data-landscape), BEFORE showModal and before .morph is
      // enabled, so the card opens at the right ratio with no morph on open.
      if (slides[idx]) dialog.classList.toggle('is-landscape', slides[idx].hasAttribute('data-landscape'));
      // Mobile = full-screen MODAL (locks the page behind). Desktop = NON-modal
      // floating card: the page stays interactive and scrollable while the video
      // plays, and there's no backdrop to click-close. (showModal makes the page
      // inert + adds a click-catching backdrop; show() does not.)
      if (vmode()) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('cc-popup-no-scroll');
      } else {
        if (typeof dialog.show === 'function') dialog.show();
        else dialog.setAttribute('open', '');
      }
      syncLogo();   // mobile: show the store logo at the top, matching nav size
      // Swipe hint: a gentle nudge. Fade IN 1s after open (only if they haven't
      // swiped yet), then fade OUT after showing for 3s. Any swipe cancels/hides
      // it. CSS gates it to mobile + multi-reel.
      if (swipeHint && vmode()) {
        // Point the hint at the only/obvious direction: on the LAST reel you can
        // only swipe DOWN to go back; otherwise UP for the next one.
        var atLast = idx >= slides.length - 1;
        var txtEl = swipeHint.querySelector('.video-reels__swipe-hint-text');
        if (txtEl) {
          var t = atLast ? swipeHint.getAttribute('data-hint-prev') : swipeHint.getAttribute('data-hint-next');
          if (t) txtEl.textContent = t;
        }
        swipeHint.classList.toggle('is-down', atLast);
        hideSwipeHint();
        swipeHintShowTimer = window.setTimeout(function () {
          swipeHint.classList.add('is-visible');
          swipeHintHideTimer = window.setTimeout(function () {
            swipeHint.classList.remove('is-visible');
          }, 3000);
        }, 1000);
      }
      // Position + size the card instantly for the first reel, THEN enable the
      // morph transition so only subsequent orientation changes (swiping) tween.
      requestAnimationFrame(function () {
        goTo(idx, false);
        requestAnimationFrame(function () { dialog.classList.add('morph'); });
      });
    }

    function close() {
      document.body.classList.remove('cc-popup-no-scroll');
      dialog.classList.remove('morph');   // next open sizes instantly again
      // Stop audio immediately and directly — don't rely only on the 'close'
      // event firing (unreliable on some mobile close paths).
      pauseAll();
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else { dialog.removeAttribute('open'); }
    }

    // Thumbnail triggers.
    root.querySelectorAll('[data-reel-index]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open(parseInt(btn.getAttribute('data-reel-index'), 10) || 0);
      });
    });

    var prev = dialog.querySelector('[data-reels-prev]');
    var next = dialog.querySelector('[data-reels-next]');
    if (prev) prev.addEventListener('click', function () { goTo(currentIndex() - 1, true); });
    if (next) next.addEventListener('click', function () { goTo(currentIndex() + 1, true); });

    var closeBtn = dialog.querySelector('[data-reels-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Arrow keys step through reels (the modal dialog has focus while open).
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIndex() - 1, true); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIndex() + 1, true); }
    });

    if (soundBtn) {
      soundBtn.addEventListener('click', function () {
        muted = !muted;
        var v = videoAt(currentIndex());
        if (v) v.muted = muted;
        soundBtn.setAttribute('data-muted', String(muted));
        soundBtn.setAttribute('aria-pressed', String(!muted));
      });
    }

    var shareBtn = dialog.querySelector('[data-reels-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var url = window.location.href;
        if (navigator.share) {
          navigator.share({ url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).catch(function () {});
        }
      });
    }

    // Tap the slide (not the <video>, which has pointer-events:none so touch
    // drags reach the carousel) to play/pause or enable sound. A drag that
    // moved the carousel is ignored so a swipe doesn't register as a tap.
    slides.forEach(function (slide) {
      var sx = 0, sy = 0, moved = false;
      slide.addEventListener('pointerdown', function (e) { sx = e.clientX; sy = e.clientY; moved = false; });
      slide.addEventListener('pointermove', function (e) { if (Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8) moved = true; });
      slide.addEventListener('click', function () { if (!moved) onVideoClick(); });
    });
    if (playbtn) playbtn.addEventListener('click', togglePlay);

    // Mobile swipe: drive the carousel directly with a transform. Works on iOS
    // (native scroll-snap was unreliable there) and consumes the gesture so the
    // page behind the modal never scrolls.
    (function mobileSwipe() {
      var startY = 0, dy = 0, swiping = false;
      track.addEventListener('touchstart', function (e) {
        if (!vmode() || !e.touches || e.touches.length !== 1) return;
        swiping = true;
        startY = e.touches[0].clientY;
        dy = 0;
        hideSwipeHint();   // first swipe dismisses the hint
        slides.forEach(function (s) { s.style.transition = 'none'; });   // follow the finger
      }, { passive: true });
      track.addEventListener('touchmove', function (e) {
        if (!swiping) return;
        dy = e.touches[0].clientY - startY;
        var atEdge = (activeIndex <= 0 && dy > 0) || (activeIndex >= slides.length - 1 && dy < 0);
        var d = atEdge ? dy * 0.3 : dy;    // rubber-band at the ends
        slides.forEach(function (s, i) {
          s.style.transform = 'translateY(calc(' + ((i - activeIndex) * 100) + '% + ' + d + 'px))';
        });
        e.preventDefault();                 // never let the page behind scroll
      }, { passive: false });
      function endSwipe() {
        if (!swiping) return;
        swiping = false;
        slides.forEach(function (s) { s.style.transition = ''; });
        var threshold = (track.clientHeight || 1) * 0.18;
        if (dy <= -threshold && activeIndex < slides.length - 1) goTo(activeIndex + 1, true);
        else if (dy >= threshold && activeIndex > 0) goTo(activeIndex - 1, true);
        else layoutMobile(false);           // not far enough -> snap back
      }
      track.addEventListener('touchend', endSwipe);
      track.addEventListener('touchcancel', endSwipe);
    })();

    // Backdrop click closes — mobile only. On desktop the popup is a non-modal
    // floating card and must NOT close when you click the page (so you can keep
    // browsing while it plays); close there is via the X / Esc only.
    dialog.addEventListener('click', function (e) {
      if (vmode() && e.target === dialog) close();
    });
    // Native dialog close (Esc / .close()) -> pause video and restore scroll.
    dialog.addEventListener('close', function () {
      pauseAll();
      document.body.classList.remove('cc-popup-no-scroll');
      dialog.classList.remove('morph');
      activeIndex = -1;   // reopen restarts from the first frame
    });
    // Esc / platform back dismisses a modal <dialog> via the 'cancel' event.
    dialog.addEventListener('cancel', pauseAll);
    // Leaving the page (tab switch, app switch, phone lock) CLOSES the pop-up, so
    // coming back lands on a clean product page — no half-paused/muted video to
    // wrestle with. Reopening a thumbnail plays fresh, with sound, in one tap.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && dialog.open) close();
    });
    // Belt-and-braces for iOS/bfcache navigations that don't fire visibilitychange.
    window.addEventListener('pagehide', function () {
      if (dialog.open) close();
    });

    // On swipe, don't thrash pause() per scroll event (that flashes posters).
    // Let the outgoing clip keep playing; re-settle once scrolling stops.
    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        var ci = currentIndex();
        playOnly(ci);
        updateNav(ci);
      }, 90);
    }, { passive: true });

    /* --- Scrubber / progress bar --------------------------------------------
       Thin dark-grey line at the bottom of the card. The white fill grows as the
       clip plays. Drag it (mouse OR touch) to seek: the big video seeks live AND
       a small frame-preview bubble follows the finger. Built in JS so it covers
       BOTH the server-rendered reel dialog and the lightbox. ------------------ */
    (function setupScrubber() {
      var scrub = document.createElement('div');
      scrub.className = 'video-reels__scrub';
      scrub.innerHTML =
        '<div class="video-reels__scrub-preview" data-scrub-preview><canvas></canvas><span data-scrub-time></span></div>' +
        '<div class="video-reels__scrub-track" data-scrub-track><div class="video-reels__scrub-fill" data-scrub-fill></div></div>';
      dialog.appendChild(scrub);

      var trackEl = scrub.querySelector('[data-scrub-track]');
      var fill = scrub.querySelector('[data-scrub-fill]');
      var preview = scrub.querySelector('[data-scrub-preview]');
      var canvas = preview.querySelector('canvas');
      var timeLbl = preview.querySelector('[data-scrub-time]');
      var dragging = false, wasPlaying = false;

      function fmt(t) {
        t = Math.max(0, t | 0);
        var m = (t / 60) | 0, s = t % 60;
        return m + ':' + (s < 10 ? '0' : '') + s;
      }
      function fracFromX(clientX) {
        var r = trackEl.getBoundingClientRect();
        if (!r.width) return 0;
        return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      }
      // Display-only canvas snapshot of the current frame. The CDN video is
      // cross-origin so the canvas is "tainted" (no pixel read-back), but drawing
      // for display is allowed. If it ever fails, the big video still shows the
      // scrubbed frame, so scrubbing never breaks.
      function drawPreview(v) {
        try {
          var w = v.videoWidth, h = v.videoHeight;
          if (!w || !h) return;
          var cw = 130, ch = Math.round(cw * h / w);
          if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
          canvas.getContext('2d').drawImage(v, 0, 0, cw, ch);
        } catch (e) {}
      }
      function positionPreview(frac) {
        var r = trackEl.getBoundingClientRect();
        var dr = dialog.getBoundingClientRect();
        var x = (r.left - dr.left) + frac * r.width;
        var half = preview.offsetWidth / 2 || 65;
        preview.style.left = Math.min(dr.width - half - 4, Math.max(half + 4, x)) + 'px';
      }
      function setFill(frac) { if (fill) fill.style.width = (frac * 100) + '%'; }

      function seekTo(frac) {
        var v = videoAt(currentIndex());
        if (!v || !isFinite(v.duration) || !v.duration) return;
        v.currentTime = frac * v.duration;
        setFill(frac);
        if (timeLbl) timeLbl.textContent = fmt(frac * v.duration);
        positionPreview(frac);
      }
      function onSeeked() { var v = videoAt(currentIndex()); if (v) drawPreview(v); }

      // Begin an actual scrub (pause, capture, show preview, seek).
      var pend = false, downX = 0, downY = 0;
      function beginScrub(e, v) {
        dragging = true;
        wasPlaying = !v.paused && !v.ended;
        v.pause();
        // Scrubbing out of the finished state clears the black screen.
        var slide = v.closest ? v.closest('[data-reel-slide]') : null;
        if (slide) slide.classList.remove('is-ended');
        scrub.classList.add('is-scrubbing');
        showPlay(false);
        v.addEventListener('seeked', onSeeked);
        seekTo(fracFromX(e.clientX));
        try { trackEl.setPointerCapture(e.pointerId); } catch (_) {}
      }
      function down(e) {
        var v = videoAt(currentIndex());
        if (!v) return;
        if (e.pointerType === 'mouse') {
          // Mouse has no scroll gesture to protect — scrub immediately.
          beginScrub(e, v);
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Touch/pen: wait to see intent. A VERTICAL swipe must fall through to
          // the carousel (TikTok up/down), so do NOT capture or preventDefault
          // here — only a horizontal drag should scrub. This stops the scrub bar
          // from swallowing swipes that start low on the screen.
          pend = true; downX = e.clientX; downY = e.clientY;
        }
      }
      function move(e) {
        if (dragging) {
          seekTo(fracFromX(e.clientX));
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (!pend) return;
        var dx = Math.abs(e.clientX - downX), dy = Math.abs(e.clientY - downY);
        if (dx < 6 && dy < 6) return;                 // too small to judge yet
        if (dx > dy) { pend = false; beginScrub(e, videoAt(currentIndex())); }  // horizontal → scrub
        else { pend = false; }                         // vertical → let the carousel scroll
      }
      function up(e) {
        if (!dragging) {
          // A clean tap on the bar jumps to that point (keeps playing).
          if (pend && Math.abs(e.clientX - downX) < 6 && Math.abs(e.clientY - downY) < 6) {
            if (videoAt(currentIndex())) seekTo(fracFromX(e.clientX));
          }
          pend = false;
          return;
        }
        dragging = false;
        pend = false;
        scrub.classList.remove('is-scrubbing');
        var v = videoAt(currentIndex());
        if (v) {
          v.removeEventListener('seeked', onSeeked);
          if (wasPlaying) { var p = v.play(); if (p && p.catch) p.catch(function () {}); showPlay(false); }
        }
        e.stopPropagation();
      }
      // Pointer events cover mouse + touch + pen (iPhone, Android, desktop).
      trackEl.addEventListener('pointerdown', down);
      trackEl.addEventListener('pointermove', move);
      trackEl.addEventListener('pointerup', up);
      trackEl.addEventListener('pointercancel', up);

      // Keep the fill in sync with playback. 'timeupdate' only fires ~4x/sec
      // (visibly steppy), so drive the fill from a requestAnimationFrame loop
      // instead — ~60fps, perfectly smooth. The loop runs only while a clip is
      // actually playing (started on 'play', stopped on 'pause'/'ended'/close).
      var rafId = null;
      function loop() {
        var v = videoAt(currentIndex());
        if (v && !dragging && isFinite(v.duration) && v.duration) setFill(v.currentTime / v.duration);
        rafId = requestAnimationFrame(loop);
      }
      function startLoop() { if (rafId == null) rafId = requestAnimationFrame(loop); }
      function stopLoop() { if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; } }
      slides.forEach(function (slide) {
        var v = slide.querySelector('video');
        if (!v) return;
        v.addEventListener('play', startLoop);
        v.addEventListener('pause', stopLoop);
        v.addEventListener('ended', function () { stopLoop(); if (v === videoAt(currentIndex())) setFill(1); });
      });
      // Belt-and-braces: stop the loop when the player closes (also removes the
      // lightbox's loop when its dialog is torn down).
      dialog.addEventListener('close', stopLoop);
    })();

    // Expose the opener so a dynamically-built instance (video lightbox) can open.
    root.openReelAt = open;
  }

  function initAll(ctx) {
    (ctx || document).querySelectorAll('[data-video-reels]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init for Theme Editor section reloads.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  /* --- Video lightbox ---------------------------------------------------
     Opens page-content videos (e.g. the size guide's .bt-size-video) in the
     SAME player, built on the fly so it reuses all the reel behaviour. The
     cc-modal clones its content, so we use a MutationObserver + delegated
     (capture-phase) click rather than per-element binding. ------------------ */
  var LB_SELECTOR = '.size-chart video, .pdp-video-zone video';

  function sourcesHtml(v) {
    var out = '';
    var sources = v.querySelectorAll('source');
    if (sources.length) {
      sources.forEach(function (s) {
        out += '<source src="' + s.getAttribute('src') + '"' +
          (s.getAttribute('type') ? ' type="' + s.getAttribute('type') + '"' : '') + '>';
      });
    } else {
      var src = v.currentSrc || v.getAttribute('src');
      if (src) out += '<source src="' + src + '">';
    }
    return out;
  }

  function lightboxControls() {
    return '' +
      '<button type="button" class="video-reels__ctrl video-reels__close" data-reels-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg></button>' +
      '<button type="button" class="video-reels__ctrl video-reels__sound" data-reels-sound data-muted="true" aria-pressed="false" aria-label="Toggle sound"><span class="is-muted"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 9v6h4l5 4V5L8 9H4z" stroke-linejoin="round"/><path d="M17 9l4 6M21 9l-4 6" stroke-linecap="round"/></svg></span><span class="is-sound"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 9v6h4l5 4V5L8 9H4z" stroke-linejoin="round"/><path d="M16.5 8.5a5 5 0 010 7M19 6a8 8 0 010 12" stroke-linecap="round"/></svg></span></button>' +
      '<button type="button" class="video-reels__ctrl video-reels__prev" data-reels-prev aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="video-reels__ctrl video-reels__next" data-reels-next aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<div class="video-reels__logo" data-reels-logo aria-hidden="true"></div>' +
      '<button type="button" class="video-reels__playbtn" data-reels-play aria-label="Play video"><svg viewBox="0 0 76 76" fill="none"><rect x="2" y="2" width="72" height="72" rx="36" fill="black" fill-opacity="0.7"/><path d="M30.0801 51.68V24.32L53.8401 38L30.0801 51.68Z" fill="white"/><rect x="1" y="1" width="74" height="74" rx="37" stroke="white" stroke-opacity="0.5" stroke-width="2"/></svg></button>';
  }

  function openLightbox(videos, startIndex) {
    var root = document.createElement('div');
    root.className = 'video-reels';
    root.setAttribute('data-video-reels', '');
    if (videos.length > 1) root.setAttribute('data-multi', '');
    var slides = videos.map(function (v) {
      var poster = v.getAttribute('poster');
      var vw = v.videoWidth || v.offsetWidth, vh = v.videoHeight || v.offsetHeight;
      var land = vw && vh && vw > vh;
      return '<div class="video-reels__slide" data-reel-slide' + (land ? ' data-landscape' : '') + '><video class="video-reels__video" muted playsinline preload="auto"' +
        (poster ? ' poster="' + poster + '"' : '') + '>' + sourcesHtml(v) + '</video></div>';
    }).join('');
    root.innerHTML = '<dialog class="video-reels__dialog" data-reels-dialog>' +
      '<div class="video-reels__viewport" data-reels-track>' + slides + '</div>' +
      lightboxControls() + '</dialog>';
    document.body.appendChild(root);
    initRoot(root);
    var dialog = root.querySelector('[data-reels-dialog]');
    // Open at the right aspect immediately (from the clicked thumbnail) so the
    // card doesn't flash portrait then snap to landscape.
    var startV = videos[startIndex || 0];
    if (startV) {
      var sw = startV.videoWidth || startV.offsetWidth;
      var sh = startV.videoHeight || startV.offsetHeight;
      if (sw && sh) dialog.classList.toggle('is-landscape', sw > sh);
    }
    // Remove only after the CSS exit transition has played (else it vanishes).
    dialog.addEventListener('close', function () {
      window.setTimeout(function () { root.remove(); }, 360);
    });
    if (typeof root.openReelAt === 'function') root.openReelAt(startIndex || 0);
  }

  function playThumb(v) {
    if (!v.muted) v.muted = true;            // keep muted autoplay allowed
    var p = v.play && v.play();
    if (p && p.catch) p.catch(function () {});
  }
  // Load (if needed) and play a thumbnail that is now on screen. preload="none"
  // keeps a hidden clip from downloading; once visible we must kick off the
  // fetch explicitly (play() alone leaves it at networkState IDLE), then play.
  function reveal(v) {
    if (v.getAttribute('preload') === 'none') v.setAttribute('preload', 'metadata');
    if (v.networkState === 0 || v.networkState === 1) { try { v.load(); } catch (e) {} }
    playThumb(v);
  }

  // Page-content video (size guide / designer's notes): autoplay muted + looped
  // in place, and open the pop-up on tap. Native mp4 (not HLS): a product page
  // already autoplays the reel strip, and the browser can only decode so many
  // videos at once, so an extra MSE/hls.js stream would stall on its poster.
  //
  // The "already set up" guard is a JS PROPERTY, not a data-* attribute: cc-modal
  // CLONES the size-guide markup and cloneNode copies attributes, so a dataset
  // flag would make the clone look done and it would never play. We also only
  // reveal() on intersection, never up front, so the hidden original (and its
  // non-faststart mp4) doesn't download in the background.
  function prepThumb(v) {
    if (v._reelPrepped) return;
    v._reelPrepped = true;
    v.removeAttribute('controls');
    v.controls = false;
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('loop', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('autoplay', '');
    v.style.cursor = 'pointer';
    v.addEventListener('loadeddata', function () { playThumb(v); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) reveal(v); });
      }, { threshold: 0.01 }).observe(v);
    } else {
      reveal(v);
    }
  }
  // The Styled & Seen reel strip keeps its native mp4 autoplay (video_tag) and is
  // left alone — only the page videos go through prepThumb.
  function scanThumbs(ctx) {
    (ctx || document).querySelectorAll(LB_SELECTOR).forEach(prepThumb);
  }
  scanThumbs();
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(LB_SELECTOR)) prepThumb(n);
        if (n.querySelectorAll) scanThumbs(n);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Delegated, capture-phase so it beats any native video / cc-modal handling.
  document.addEventListener('click', function (e) {
    var v = e.target.closest && e.target.closest(LB_SELECTOR);
    if (!v) return;
    e.preventDefault();
    e.stopPropagation();
    var container = v.closest('.size-chart, .pdp-video-zone') || document;
    var all = Array.prototype.slice.call(container.querySelectorAll(LB_SELECTOR));
    var idx = all.indexOf(v);
    openLightbox(all, idx < 0 ? 0 : idx);
  }, true);

  /* --- Scroll-hint fade for the thumbnail strip (desktop) ---
     Toggle .hint-right while there is more to scroll to the right. ---------- */
  function initThumbHint(strip) {
    if (strip.dataset.hintInit) return;
    strip.dataset.hintInit = '1';
    var heading = strip.previousElementSibling;
    // Measure the content gutter from the heading so the mobile full-bleed
    // lines the first thumb up with the heading exactly.
    function setGutter() {
      if (heading && heading.classList && heading.classList.contains('video-reels__heading')) {
        var g = Math.max(0, Math.round(heading.getBoundingClientRect().left));
        strip.style.setProperty('--content-gutter', g + 'px');
      }
    }
    function update() {
      var max = strip.scrollWidth - strip.clientWidth;
      strip.classList.toggle('hint-right', strip.scrollLeft < max - 2);
      strip.classList.toggle('hint-left', strip.scrollLeft > 2);
    }
    setGutter();
    update();
    strip.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', function () { setGutter(); update(); });
    // Re-check once media (which can widen the strip) has loaded.
    window.setTimeout(function () { setGutter(); update(); }, 600);
  }
  function scanThumbHints(ctx) {
    (ctx || document).querySelectorAll('.video-reels__thumbs').forEach(initThumbHint);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanThumbHints(); });
  } else {
    scanThumbHints();
  }
  document.addEventListener('shopify:section:load', function (e) { scanThumbHints(e.target); });
})();
